import { useMemo } from 'react';
import { diasSinAnotarPorLocal, periodOfMonth } from '@alexiarest/shared';
import { useDrafts, useIngredients, useMe, useMenuItems, usePayables, useRecipes, useRevenuesDeTodos, useSettings } from '../api/hooks';
import { veSeccion, type Acceso } from '../auth/roles';
import { today } from '../format';

/**
 * Numero de cosas pendientes que el menu ensenya junto a cada pantalla.
 * Ambar = algo por confirmar; rojo = dinero en riesgo. Siempre con su texto
 * (va en el title y para lectores de pantalla), nunca solo el color.
 */
export interface ContadorMenu {
  n: number;
  tono: 'warn' | 'bad';
  titulo: string;
}

/**
 * Los contadores salen de consultas que la app ya hace en otras pantallas
 * (borradores, ingredientes, cuentas por pagar), asi que casi siempre estan
 * en cache. Solo se piden si el usuario ve esa seccion.
 */
export function useContadoresMenu(acceso: Acceso, listo: boolean): Partial<Record<string, ContadorMenu>> {
  const veAlexia = listo && (veSeccion(acceso, 'asistente') || veSeccion(acceso, 'recetas') || veSeccion(acceso, 'gastos'));
  const veIngredientes = listo && veSeccion(acceso, 'ingredientes');
  const veGastos = listo && veSeccion(acceso, 'gastos');
  const drafts = useDrafts(veAlexia);
  const ingredients = useIngredients(veIngredientes);
  const payables = usePayables(today(), veGastos);
  // Ventas · N: días pasados de este mes sin anotar ni cerrados, de TODOS
  // los locales que ve la persona, aunque esté mirando uno (bloque C).
  const veVentas = listo && veSeccion(acceso, 'ingresos');
  const mes = today().slice(0, 7);
  const revenues = useRevenuesDeTodos(useMemo(() => periodOfMonth(mes), [mes]), veVentas);
  const settings = useSettings(veVentas);
  const me = useMe();

  const porConfirmar = (drafts.data ?? []).filter((d) => d.status === 'pending').length;
  const precios = (ingredients.data ?? []).filter((i) => i.active && i.pricePending).length;
  const vencidas = (payables.data?.items ?? []).filter((p) => p.status === 'vencida').length;

  // Tu carta · N: platos sin precio en la carta o sin ingredientes.
  const veCarta = listo && veSeccion(acceso, 'recetas');
  const recetas = useRecipes(veCarta);
  const carta = useMenuItems(veCarta);
  const sinTerminar = veCarta && recetas.data && carta.data
    ? recetas.data.filter((r) => r.kind === 'plato' && r.active !== false && (!r.components.length || !carta.data!.some((m) => m.recipeId === r.recipeId))).length
    : 0;

  const locales = (me.data?.locations ?? []).filter((l) => l.active);
  const faltan = veVentas && revenues.data && settings.data && revenues.data.length > 0
    ? diasSinAnotarPorLocal(mes, revenues.data, locales.length ? locales : [{ tenantId: '', locationId: 'principal', name: '', active: true }], settings.data, today())
      .reduce((n, l) => n + l.dias.length, 0)
    : 0;

  const out: Partial<Record<string, ContadorMenu>> = {};
  if (sinTerminar) out['/carta'] = { n: sinTerminar, tono: 'warn', titulo: `${sinTerminar} ${sinTerminar === 1 ? 'plato sin terminar' : 'platos sin terminar'}` };
  if (faltan) out['/ingresos'] = { n: faltan, tono: 'warn', titulo: `${faltan} ${faltan === 1 ? 'día' : 'días'} sin anotar` };
  if (veAlexia && porConfirmar) out['/alexia'] = { n: porConfirmar, tono: 'warn', titulo: `${porConfirmar} por confirmar` };
  if (veIngredientes && precios) out['/ingredientes'] = { n: precios, tono: 'warn', titulo: `${precios} ${precios === 1 ? 'precio' : 'precios'} por confirmar` };
  if (veGastos && vencidas) out['/gastos'] = { n: vencidas, tono: 'bad', titulo: `${vencidas} ${vencidas === 1 ? 'factura vencida' : 'facturas vencidas'}` };
  return out;
}
